// Markup from design/kit/components/Viewer: the centered file viewer (scrim, modal vmodal, v-head
// with the file name and subline, v-body on its grey ground, m-foot with Download and Close).
// PDFs are drawn with pdf.js, loaded only when a viewer opens. Like the browser's own PDF viewer
// (Yehuda, Oct 25): every page in one scrolling column, "Page 3 of 9" following the scroll, zoom
// out and in, and Open in new tab. Pages are drawn when they come near the view, so a long file
// stays light. Esc and the scrim close it, Tab stays inside, and the page behind does not scroll.
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import { DownloadIcon, ExternalLinkIcon, MinusIcon, PlusIcon, XIcon } from './icons';
import { useModalLayer } from './modal-layer';

export type ViewerFile = {
  /** The file name, as kept on the renewal. */
  title: string;
  /** "Insured | Carrier | Line | Policy #" */
  subline: string;
  src: string;
  downloadHref: string;
  kind: 'pdf' | 'image';
};

type ViewerProps = ViewerFile & {
  onClose: () => void;
  /** Opened over a panel (the Quick view): the scrim goes above it so the panel is not clickable. */
  above?: boolean;
};

/** Zoom steps, as a share of the viewer's width (1 = the page fills the width). */
const zoomSteps = [0.5, 0.75, 1, 1.25, 1.5, 2, 3];
const fitStep = zoomSteps.indexOf(1);

const zoomLabel = (z: number) => `${Math.round(z * 100)}%`;

type Size = { w: number; h: number };

export function Viewer({ title, subline, src, downloadHref, kind, onClose, above }: ViewerProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [sizes, setSizes] = useState<Size[] | null>(null);
  const [page, setPage] = useState(1);
  const [step, setStep] = useState(fitStep);
  const [width, setWidth] = useState(0);
  const [failed, setFailed] = useState(false);
  // Where the reader was (share of the scroll), kept across a zoom.
  const keepAt = useRef<number | null>(null);
  useModalLayer(boxRef);
  const zoom = zoomSteps[step];

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    boxRef.current?.focus();
    return () => opener?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // The width a page gets at 100%: the grey area inside its padding.
  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    const measure = () => {
      const s = getComputedStyle(body);
      setWidth(body.clientWidth - parseFloat(s.paddingLeft) - parseFloat(s.paddingRight));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(body);
    return () => ro.disconnect();
  }, []);

  // Open the PDF once and read each page's shape, so the column has its full height at once.
  useEffect(() => {
    if (kind !== 'pdf') return;
    let cancelled = false;
    let destroy: (() => Promise<void>) | null = null;
    (async () => {
      try {
        const pdfjs = await import('pdfjs-dist');
        const { default: workerSrc } = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
        pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;
        // The decoders and the standard fonts pdf.js loads at run time, served under /pdfjs/ (see
        // vite.config.ts). Without them a CCITT fax image (NYCM's ID card front) drew blank.
        const assets = `${import.meta.env.BASE_URL}pdfjs/`;
        const task = pdfjs.getDocument({
          url: src,
          wasmUrl: `${assets}wasm/`,
          standardFontDataUrl: `${assets}standard_fonts/`,
        });
        destroy = () => task.destroy();
        const pdf = await task.promise;
        const shapes: Size[] = [];
        for (let n = 1; n <= pdf.numPages; n++) {
          const v = (await pdf.getPage(n)).getViewport({ scale: 1 });
          shapes.push({ w: v.width, h: v.height });
        }
        if (cancelled) return;
        setDoc(pdf);
        setSizes(shapes);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      void destroy?.();
    };
  }, [kind, src]);

  // "Page N of M": the page that holds the middle of the view.
  const onScroll = useCallback(() => {
    const body = bodyRef.current;
    if (!body) return;
    const middle = body.scrollTop + body.clientHeight / 2;
    const pages = body.querySelectorAll<HTMLElement>('[data-page]');
    let at = 1;
    for (const el of pages) {
      if (el.offsetTop <= middle) at = Number(el.dataset.page);
      else break;
    }
    setPage(at);
  }, []);

  function zoomTo(next: number) {
    const body = bodyRef.current;
    if (body && body.scrollHeight > 0) keepAt.current = body.scrollTop / body.scrollHeight;
    setStep(Math.max(0, Math.min(zoomSteps.length - 1, next)));
  }

  // After a zoom, stay on the same spot of the file.
  useLayoutEffect(() => {
    const body = bodyRef.current;
    if (!body || keepAt.current === null) return;
    body.scrollTop = keepAt.current * body.scrollHeight;
    keepAt.current = null;
  }, [step]);

  const pageWidth = Math.max(0, Math.floor(width * zoom));
  const count = sizes?.length ?? 0;

  return (
    <>
      <button
        type="button"
        className={above ? 'scrim top' : 'scrim'}
        aria-label={`Close ${title}`}
        onClick={onClose}
      />
      <div
        ref={boxRef}
        className="modal vmodal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="viewer-title"
        tabIndex={-1}
      >
        <div className="v-head">
          <div className="col g2 grow" style={{ minWidth: 0 }}>
            <h2 id="viewer-title" className="dtitle ell">
              {title}
            </h2>
            <span className="t12 muted ell">{subline}</span>
          </div>
          {count > 0 && (
            <span className="t12 text2 v-pn" aria-live="polite">
              Page {page} of {count}
            </span>
          )}
          {!failed && (
            <div className="v-pg" role="group" aria-label="Zoom">
              <button
                type="button"
                className="xbtn"
                aria-label="Zoom out"
                disabled={step === 0}
                onClick={() => zoomTo(step - 1)}
              >
                <MinusIcon />
              </button>
              <button
                type="button"
                className="xbtn t12 text2"
                style={{ width: 'auto', paddingInline: 'var(--space-6)' }}
                aria-label="Fit to width"
                title="Fit to width"
                onClick={() => zoomTo(fitStep)}
              >
                {zoomLabel(zoom)}
              </button>
              <button
                type="button"
                className="xbtn"
                aria-label="Zoom in"
                disabled={step === zoomSteps.length - 1}
                onClick={() => zoomTo(step + 1)}
              >
                <PlusIcon />
              </button>
            </div>
          )}
          <button type="button" className="xbtn" aria-label="Close" onClick={onClose}>
            <XIcon />
          </button>
        </div>
        <div
          ref={bodyRef}
          className="v-body"
          // The column scrolls both ways once a page is wider than the viewer.
          style={{ overflowX: 'auto', alignItems: zoom < 1 ? 'center' : 'flex-start' }}
          tabIndex={0}
          aria-label={`${title}, scroll to read`}
          onScroll={onScroll}
        >
          {failed ? (
            <div className="fsx amber">
              <div className="fsx-h">
                <span>This file could not be shown here. Use Open in new tab or Download.</span>
              </div>
            </div>
          ) : kind === 'image' ? (
            <div
              className="pdf-page"
              style={{ flex: 'none', padding: 0, overflow: 'hidden', width: `${zoom * 100}%` }}
            >
              <img src={src} alt={title} style={{ width: '100%', display: 'block' }} />
            </div>
          ) : !doc || !sizes || pageWidth === 0 ? (
            <span className="readline">
              <span className="spin" />
              Opening the file
            </span>
          ) : (
            sizes.map((size, i) => (
              <PdfPage
                key={i}
                doc={doc}
                n={i + 1}
                size={size}
                cssWidth={pageWidth}
                root={bodyRef}
              />
            ))
          )}
        </div>
        <div className="m-foot">
          <a className="btn" href={downloadHref} download>
            <DownloadIcon />
            Download
          </a>
          <a className="btn" href={src} target="_blank" rel="noopener noreferrer">
            <ExternalLinkIcon />
            Open in new tab
          </a>
          <button type="button" className="btn ml-auto" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </>
  );
}

/** One page: its full size held from the start, drawn once it comes near the view. */
function PdfPage({
  doc,
  n,
  size,
  cssWidth,
  root,
}: {
  doc: PDFDocumentProxy;
  n: number;
  size: Size;
  cssWidth: number;
  root: RefObject<HTMLDivElement | null>;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [near, setNear] = useState(false);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    // A screen above and below the view, so scrolling never meets a blank page.
    const io = new IntersectionObserver(([e]) => e.isIntersecting && setNear(true), {
      root: root.current,
      rootMargin: '100% 0%',
    });
    io.observe(el);
    return () => io.disconnect();
  }, [root]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!near || !canvas || cssWidth === 0) return;
    let task: RenderTask | null = null;
    let cancelled = false;
    (async () => {
      try {
        const p = await doc.getPage(n);
        if (cancelled) return;
        const ratio = window.devicePixelRatio || 1;
        const viewport = p.getViewport({ scale: (cssWidth / size.w) * ratio });
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        task = p.render({ canvas, viewport });
        await task.promise;
      } catch {
        // A page whose drawing was replaced by a newer zoom is simply dropped.
      }
    })();
    return () => {
      cancelled = true;
      task?.cancel();
    };
  }, [doc, n, size.w, cssWidth, near]);

  return (
    <div
      ref={boxRef}
      data-page={n}
      className="pdf-page"
      style={{
        flex: 'none',
        padding: 0,
        overflow: 'hidden',
        width: `${cssWidth}px`,
        aspectRatio: `${size.w} / ${size.h}`,
      }}
    >
      <canvas
        ref={canvasRef}
        aria-label={`Page ${n}`}
        style={{ width: '100%', height: '100%', display: 'block' }}
      />
    </div>
  );
}
